import { Link } from 'react-router-dom';
import { MessageCircle, Star, Sparkles, Rocket } from 'lucide-react';
import { fallbackAvatarUrl } from '../utils/fallbackAvatar';

const TIER_NAME_COLORS = {
  free: 'text-white',
  premium: 'text-[#F59E0B]',
  ultimate: 'text-[#E11D48]',
};

const TIER_BORDER_COLORS = {
  free: 'border-white/5 hover:border-white/10',
  premium: 'border-[#F59E0B]/20 hover:border-[#F59E0B]/40',
  ultimate: 'border-[#E11D48]/20 hover:border-[#E11D48]/40',
};

export function CharacterCard({ character }) {
  const defaultAvatar = fallbackAvatarUrl(character.name);
  const tier = character.creator_tier || 'free';
  const nameColor = TIER_NAME_COLORS[tier] || TIER_NAME_COLORS.free;
  const borderColor = TIER_BORDER_COLORS[tier] || TIER_BORDER_COLORS.free;

  const formatCount = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count?.toString() || '0';
  };

  return (
    <Link
      to={`/character/${character.id}`}
      className={`character-card group block bg-[#0F1115] rounded-lg overflow-hidden border transition-all duration-300 hover:-translate-y-1 ${borderColor}`}
      data-testid={`character-card-${character.id}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <img
          src={character.avatar_url || defaultAvatar}
          alt={character.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => { e.target.src = defaultAvatar; }}
        />
        {/* Boosted indicator */}
        {character.is_boosted && (
          <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-purple-500/90 backdrop-blur-sm rounded-full flex items-center gap-1">
            <Rocket className="w-3 h-3 text-white" />
            <span className="text-[9px] font-bold text-white">BOOSTED</span>
          </div>
        )}
        <div className="absolute bottom-3 left-3 flex items-center gap-2">
          <div className="flex items-center gap-1 px-2 py-1 bg-black/70 backdrop-blur-sm rounded-full">
            <MessageCircle className="w-3 h-3 text-white" />
            <span className="text-[11px] font-medium text-white">{formatCount(character.chat_count)}</span>
          </div>
          {character.avg_rating > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 bg-black/70 backdrop-blur-sm rounded-full">
              <Star className="w-3 h-3 text-[#F59E0B] fill-[#F59E0B]" />
              <span className="text-[11px] font-medium text-white">{character.avg_rating}</span>
            </div>
          )}
        </div>
      </div>
      <div className="p-3">
        <h3 className={`font-bold text-sm leading-tight mb-1 line-clamp-1 font-['Unbounded'] ${nameColor}`}>
          {character.name}
        </h3>
        <p className="text-[#A1A1AA] text-xs leading-relaxed line-clamp-2 mb-2">
          {character.description}
        </p>
        {character.tags && character.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {character.tags.slice(0, 3).map((tag, index) => (
              <span key={index} className="px-2 py-0.5 text-[10px] bg-[#1A1D24] border border-white/10 rounded-full text-[#71717A] capitalize">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}

export function CharacterCardSkeleton() {
  return (
    <div className="bg-[#0F1115] rounded-lg overflow-hidden border border-white/5">
      <div className="aspect-[4/5] skeleton" />
      <div className="p-3 space-y-2">
        <div className="h-4 w-3/4 skeleton rounded" />
        <div className="h-3 w-full skeleton rounded" />
        <div className="h-3 w-2/3 skeleton rounded" />
      </div>
    </div>
  );
}
